import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ClientDossier from "@/components/ClientDossier";
import ClockInOutPanel from "@/components/ClockInOutPanel";
import CreateJobDialog from "@/components/CreateJobDialog";
import { compressImage } from "@/lib/compressImage";
import {
    ArrowLeft, MapPin, Clock, User, CheckCircle2, Play, Square,
    Camera, Send, Trash2, MessageSquare, Flag, ChevronDown, Repeat, Download, Pencil,
    StickyNote, AlertOctagon, X, BadgeCheck, Plus, CheckSquare, Square as SquareIcon, ChevronLeft, ChevronRight, Hourglass
} from "lucide-react";

const statusLabel = { scheduled: "Scheduled", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled" };

function fmtDT(iso) {
    return new Date(iso).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function fmtTime(iso) {
    return new Date(iso).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function JobDetailPage() {
    const { jobId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [comments, setComments] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [operatives, setOperatives] = useState([]);
    const [client, setClient] = useState(null);
    // Multi-day span info: { series_id, my_position, days: [{id,date,status}] }
    // Empty (`days:[]`) when this isn't a span — buttons hide accordingly.
    const [spanInfo, setSpanInfo] = useState({ days: [] });
    const [newComment, setNewComment] = useState("");
    const [uploading, setUploading] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [quickNoteOpen, setQuickNoteOpen] = useState(false);
    // Photo gallery UI state: lightbox index + bulk-select mode + chosen ids.
    const [lightboxIdx, setLightboxIdx] = useState(-1);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedPhotos, setSelectedPhotos] = useState([]);
    const [downloadingZip, setDownloadingZip] = useState(false);
    // Hidden file input used by the checklist "attach photo & tick" flow.
    // `pendingPhotoItem` remembers which item is waiting for the photo so
    // the onChange handler can route the resulting photo to the right tick.
    const checklistPhotoInputRef = useRef(null);
    const [pendingPhotoItem, setPendingPhotoItem] = useState(null);
    const [checklistBusyItem, setChecklistBusyItem] = useState(null);
    const fileRef = useRef(null);

    const isManager = user?.role === "manager";
    const isOwner = !!user?.is_workspace_owner;

    const load = async () => {
        try {
            const [j, c, p] = await Promise.all([
                api.get(`/jobs/${jobId}`),
                api.get(`/jobs/${jobId}/comments`),
                api.get(`/jobs/${jobId}/photos`),
            ]);
            setJob(j.data); setComments(c.data); setPhotos(p.data);
            if (j.data.client_id) {
                try {
                    const cr = await api.get(`/clients/${j.data.client_id}`);
                    setClient(cr.data);
                } catch { setClient(null); }
            } else {
                setClient(null);
            }
            if (isManager) {
                const u = await api.get("/users");
                setOperatives(u.data);
            }
            // Fetch span info in the background — a failure just means the
            // extend/truncate row won't render, which is fine.
            try {
                const s = await api.get(`/jobs/${jobId}/span`);
                setSpanInfo(s.data || { days: [] });
            } catch { setSpanInfo({ days: [] }); }
        } catch (e) {
            toast.error(formatApiError(e));
            navigate("/");
        }
    };

    useEffect(() => { load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!job) {
        return <div className="text-sm text-[var(--text-muted)]">Loading job…</div>;
    }

    const changeStatus = async (status) => {
        setStatusOpen(false);
        try {
            const { data } = await api.patch(`/jobs/${job.id}`, { status });
            setJob(data);
            toast.success("Status updated");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const assign = async (userId) => {
        setAssignOpen(false);
        try {
            const { data } = await api.patch(`/jobs/${job.id}`, { assigned_to: userId || null });
            setJob(data);
            toast.success(userId ? "Operative assigned" : "Unassigned");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const extendSpan = async () => {
        try {
            await api.post(`/jobs/${job.id}/span/extend`);
            const s = await api.get(`/jobs/${job.id}/span`);
            setSpanInfo(s.data || { days: [] });
            toast.success("Added another day");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const truncateSpan = async () => {
        const daysAfter = spanInfo.days.length - (spanInfo.my_position || 1);
        if (daysAfter <= 0) return;
        const ok = window.confirm(
            `Remove the ${daysAfter} day${daysAfter === 1 ? "" : "s"} after this one from the span?\n\n` +
            `Everything scheduled after ${new Date(job.scheduled_at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} will be deleted. This day and the days before it stay.`
        );
        if (!ok) return;
        try {
            const { data } = await api.post(`/jobs/${job.id}/span/truncate`);
            const s = await api.get(`/jobs/${job.id}/span`);
            setSpanInfo(s.data || { days: [] });
            toast.success(`Removed ${data.removed} day${data.removed === 1 ? "" : "s"}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleChecklist = async (item, done, photoId = null) => {
        try {
            const body = { item, done };
            if (photoId) body.photo_id = photoId;
            const { data } = await api.post(`/jobs/${job.id}/checklist`, body);
            setJob(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Upload a single File as a job photo and return the saved photo
    // record. Reused by the checklist "📷 Photo required" tick flow —
    // the item is only advanced AFTER a photo is attached successfully.
    const uploadChecklistPhoto = async (file, item) => {
        if (!file) return null;
        if (file.size > 25 * 1024 * 1024) {
            toast.error("Photo is too large (25MB limit)");
            return null;
        }
        let dataUrl;
        try {
            const compressed = await compressImage(file, { maxDim: 1600, quality: 0.85 });
            dataUrl = compressed.dataUrl;
        } catch {
            dataUrl = await new Promise((res, rej) => {
                const r = new FileReader();
                r.onload = () => res(r.result);
                r.onerror = rej;
                r.readAsDataURL(file);
            });
        }
        try {
            const { data } = await api.post(`/jobs/${job.id}/photos`, {
                data: dataUrl, kind: "checklist", caption: item,
            });
            setPhotos(prev => [...prev, data]);
            return data;
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't upload photo");
            return null;
        }
    };

    const postComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        try {
            const { data } = await api.post(`/jobs/${job.id}/comments`, { body: newComment.trim() });
            setComments(prev => [...prev, data]);
            setNewComment("");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const onFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        let uploaded = 0;
        let skipped = 0;
        try {
            for (const file of files) {
                if (file.size > 25 * 1024 * 1024) {
                    skipped += 1;
                    continue; // silently skip; we'll toast a summary at the end
                }
                let dataUrl;
                const caption = file.name;
                try {
                    const compressed = await compressImage(file, { maxDim: 1600, quality: 0.85 });
                    dataUrl = compressed.dataUrl;
                } catch (compressErr) {
                    dataUrl = await new Promise((res, rej) => {
                        const r = new FileReader();
                        r.onload = () => res(r.result);
                        r.onerror = rej;
                        r.readAsDataURL(file);
                    });
                }
                try {
                    const { data } = await api.post(`/jobs/${job.id}/photos`, {
                        data: dataUrl, kind: "after", caption,
                    });
                    setPhotos(prev => [...prev, data]);
                    uploaded += 1;
                } catch (uploadErr) {
                    skipped += 1;
                }
            }
            if (uploaded > 0) {
                toast.success(uploaded === 1 ? "Photo uploaded" : `${uploaded} photos uploaded`);
            }
            if (skipped > 0) {
                toast.error(`${skipped} photo${skipped === 1 ? "" : "s"} skipped (too large or upload failed)`);
            }
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const deletePhoto = async (photoId) => {
        try {
            await api.delete(`/jobs/${job.id}/photos/${photoId}`);
            setPhotos(prev => prev.filter(p => p.id !== photoId));
            toast.success("Photo removed");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const downloadAlbum = async () => {
        setDownloadingPdf(true);
        try {
            const res = await api.get(`/jobs/${job.id}/photo-album.pdf`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safe = (job.title || "job").replace(/[^a-zA-Z0-9_\- ]/g, "").trim().replace(/\s+/g, "_") || "job";
            a.download = `${safe}_photos.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't build album");
        } finally { setDownloadingPdf(false); }
    };

    // Single-photo download via the authenticated blob endpoint. Fetching as
    // a Blob (rather than a bare <a download>) is required because the blob
    // route is cookie-protected and some mobile browsers skip credentials on
    // simple anchor clicks.
    const downloadSinglePhoto = async (photo) => {
        try {
            const res = await api.get(`/photos/${photo.id}/blob`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const ct = res.data.type || photo.content_type || "image/jpeg";
            const ext = ct.includes("png") ? "png" : ct.includes("webp") ? "webp" : ct.includes("heic") ? "heic" : "jpg";
            const safeAuthor = (photo.author_name || "photo").replace(/[^a-zA-Z0-9_\- ]/g, "").replace(/\s+/g, "_").slice(0, 24);
            const stamp = photo.created_at ? new Date(photo.created_at).toISOString().slice(0, 16).replace(/[:T]/g, "") : "";
            a.download = `${safeAuthor}${stamp ? "_" + stamp : ""}.${ext}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't download photo");
        }
    };

    const togglePhotoSelected = (photoId) => {
        setSelectedPhotos(prev => prev.includes(photoId) ? prev.filter(x => x !== photoId) : [...prev, photoId]);
    };

    const downloadSelectedZip = async () => {
        if (!selectedPhotos.length) return;
        setDownloadingZip(true);
        try {
            const res = await api.get(
                `/jobs/${job.id}/photos.zip?ids=${selectedPhotos.join(",")}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const safe = (job.title || "job").replace(/[^a-zA-Z0-9_\- ]/g, "").trim().replace(/\s+/g, "_") || "job";
            a.download = `${safe}_selected_photos.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Downloaded ${selectedPhotos.length} photo${selectedPhotos.length === 1 ? "" : "s"}`);
            setSelectMode(false);
            setSelectedPhotos([]);
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't build download");
        } finally { setDownloadingZip(false); }
    };

    const copyToWip = async () => {
        if (!window.confirm("Copy this job to Work in progress? It stays on the schedule too.")) return;
        try {
            const { data } = await api.post(`/jobs/${job.id}/duplicate-to-wip`);
            setJob(prev => ({ ...prev, wip_copy_id: data.id }));
            toast.success("Copied to Work in progress");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deleteJob = async () => {
        // Recurring series get a two-step prompt: delete just this one, or
        // stop the whole series going forward. Feels natural (like Google
        // Calendar) and stops a manager wiping out a year of bookings by
        // accident.
        const isSeries = job.recurrence_kind && job.recurrence_kind !== "none";
        let deleteSeries = false;
        if (isSeries) {
            const wholeSeries = confirm(
                "This job is part of a repeating series.\n\n" +
                "OK  = delete THIS job + every future one in the series (past occurrences stay in history)\n" +
                "Cancel = delete only this one occurrence"
            );
            deleteSeries = wholeSeries;
            if (!deleteSeries) {
                if (!confirm("Delete just this one occurrence? This cannot be undone.")) return;
            }
        } else {
            if (!confirm("Delete this job? This cannot be undone.")) return;
        }
        try {
            const url = deleteSeries
                ? `/jobs/${job.id}?delete_series=true`
                : `/jobs/${job.id}`;
            const { data } = await api.delete(url);
            if (deleteSeries) {
                toast.success(`Series stopped — ${data?.deleted || 1} future job${(data?.deleted || 1) === 1 ? "" : "s"} removed`);
            } else {
                toast.success("Job deleted");
            }
            navigate("/manager");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const done = new Set(job.checklist_done || []);

    // Checklist tick handler used by individual item buttons. Opens the
    // hidden file-picker when the item is photo-required and no photo has
    // been attached yet; otherwise advances the counter straight away.
    const tickChecklistItem = async (item) => {
        const counts = job.checklist_counts || {};
        const progress = job.checklist_progress || {};
        const target = Math.max(1, Number(counts[item] || 1));
        const current = Math.max(0, Number(progress[item] || 0));
        if (current >= target) return; // already full
        const photoRequired = (job.checklist_photo_required || []).includes(item);
        const alreadyHasPhoto = Boolean((job.checklist_item_photos || {})[item]);
        if (photoRequired && !alreadyHasPhoto && current === 0) {
            setPendingPhotoItem(item);
            if (checklistPhotoInputRef.current) {
                checklistPhotoInputRef.current.value = "";
                checklistPhotoInputRef.current.click();
            }
            return;
        }
        setChecklistBusyItem(item);
        try {
            await toggleChecklist(item, true);
        } finally {
            setChecklistBusyItem(null);
        }
    };

    const resetChecklistItem = async (item) => {
        setChecklistBusyItem(item);
        try {
            await toggleChecklist(item, false);
        } finally {
            setChecklistBusyItem(null);
        }
    };

    const onChecklistPhotoFile = async (e) => {
        const file = e.target.files?.[0];
        const item = pendingPhotoItem;
        setPendingPhotoItem(null);
        if (!file || !item) return;
        setChecklistBusyItem(item);
        try {
            const uploaded = await uploadChecklistPhoto(file, item);
            if (uploaded?.id) {
                await toggleChecklist(item, true, uploaded.id);
            }
        } finally {
            setChecklistBusyItem(null);
            if (checklistPhotoInputRef.current) checklistPhotoInputRef.current.value = "";
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <button onClick={() => navigate(-1)}
                    data-testid="back-btn"
                    className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--pine)] transition-colors">
                <ArrowLeft size={14} /> Back
            </button>

            {/* Header card */}
            <div className="card-flat p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-4">
                    <div className="min-w-0 flex-1 order-2 sm:order-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`pill status-${job.status}`}>{statusLabel[job.status]}</span>
                            {job.priority === "high" && <span className="pill priority-high"><Flag size={12} /> High priority</span>}
                            {job.recurrence_days > 0 && (
                                <span className="pill" style={{ background: "var(--pine-light)", color: "var(--pine)" }}>
                                    <Repeat size={12} /> Repeats every {job.recurrence_days} day{job.recurrence_days === 1 ? "" : "s"}
                                </span>
                            )}
                        </div>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-3" data-testid="job-title">
                            {job.title}
                        </h1>
                        <p className="text-[var(--text-secondary)] mt-1">{job.client_name}</p>
                    </div>

                    <div className="flex flex-wrap gap-2 relative order-1 sm:order-2">
                        <button
                            data-testid="quick-note-btn"
                            onClick={() => setQuickNoteOpen(true)}
                            className="btn-ghost inline-flex items-center gap-1.5 text-sm"
                            title="Add a quick note or flag an issue">
                            <StickyNote size={14} /> Quick note
                        </button>
                        <div className="relative">
                            <button data-testid="status-menu-btn" onClick={() => setStatusOpen(v => !v)}
                                    className="btn-ghost inline-flex items-center gap-1.5 text-sm">
                                Status <ChevronDown size={14} />
                            </button>
                            {statusOpen && (
                                <div className="absolute left-0 mt-2 w-48 bg-white border border-[var(--border-soft)] rounded-xl shadow-lg p-1 z-10">
                                    {[
                                        { s: "scheduled", label: "Scheduled", icon: <Square size={14} /> },
                                        { s: "in_progress", label: "In progress", icon: <Play size={14} /> },
                                        { s: "completed", label: "Completed", icon: <CheckCircle2 size={14} /> },
                                        ...(isManager ? [
                                            ...(isOwner ? [{ s: "finalised", label: "Finalised", icon: <BadgeCheck size={14} /> }] : []),
                                            { s: "cancelled", label: "Cancelled", icon: <Trash2 size={14} /> },
                                        ] : []),
                                    ].map(o => (
                                        <button key={o.s}
                                                data-testid={`set-status-${o.s}`}
                                                onClick={() => changeStatus(o.s)}
                                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-[var(--bg-subtle)] text-left">
                                            {o.icon} {o.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {isManager && (
                            <>
                                <button
                                    data-testid="edit-job-btn"
                                    onClick={() => setEditOpen(true)}
                                    className="btn-ghost inline-flex items-center gap-1.5 text-sm">
                                    <Pencil size={14} /> Edit
                                </button>
                                <div className="relative">
                                    <button data-testid="assign-menu-btn" onClick={() => setAssignOpen(v => !v)}
                                            className="btn-ghost inline-flex items-center gap-1.5 text-sm">
                                        Assign <ChevronDown size={14} />
                                    </button>
                                    {assignOpen && (
                                        <div className="absolute right-0 mt-2 w-56 bg-white border border-[var(--border-soft)] rounded-xl shadow-lg p-1 z-10 max-h-64 overflow-y-auto">
                                            <button onClick={() => assign(null)}
                                                    className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-[var(--bg-subtle)]">
                                                Unassigned
                                            </button>
                                            {operatives.map(o => (
                                                <button key={o.id}
                                                        data-testid={`assign-to-${o.id}`}
                                                        onClick={() => assign(o.id)}
                                                        className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-[var(--bg-subtle)]">
                                                    {o.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {!job.is_wip && !job.is_pipeline && (
                                    <button data-testid="copy-to-wip-btn" onClick={copyToWip}
                                            className="btn-ghost inline-flex items-center gap-1.5 text-sm">
                                        <Hourglass size={14} /> Copy to WIP
                                    </button>
                                )}
                                <button data-testid="delete-job-btn" onClick={deleteJob}
                                        className="btn-ghost !text-red-600 !border-red-100 hover:!bg-red-50 text-sm">
                                    <Trash2 size={14} />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Multi-day span row — badge showing which day this is, plus
                    manager-only "Add another day" / "End the span here"
                    buttons. Hidden entirely when the job isn't a span. */}
                {spanInfo.days.length > 1 && (
                    <div data-testid="span-controls"
                         className="mt-4 flex flex-wrap items-center gap-2 p-3 rounded-xl border border-[var(--border-soft)] bg-[var(--pine-light)]">
                        <div className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--pine)]">
                            <Repeat size={14} />
                            Day {spanInfo.my_position} of {spanInfo.days.length}
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] hidden sm:block">
                            · {spanInfo.days[0].date} → {spanInfo.days[spanInfo.days.length - 1].date}
                        </div>
                        {isManager && (
                            <div className="ml-auto flex flex-wrap gap-2">
                                <button
                                    data-testid="span-extend-btn"
                                    onClick={extendSpan}
                                    className="btn-ghost text-xs inline-flex items-center gap-1">
                                    <Plus size={12} /> Add another day
                                </button>
                                {spanInfo.days.length - spanInfo.my_position > 0 && (
                                    <button
                                        data-testid="span-truncate-btn"
                                        onClick={truncateSpan}
                                        title={`Delete the ${spanInfo.days.length - spanInfo.my_position} day${spanInfo.days.length - spanInfo.my_position === 1 ? "" : "s"} after this one`}
                                        className="btn-ghost text-xs !text-[var(--terracotta)] !border-[var(--terracotta)]/40 hover:!bg-[var(--terracotta-light)] inline-flex items-center gap-1">
                                        <Trash2 size={12} /> End span here
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {job.wip_copy_id && !job.is_wip && job.status !== "finalised" && (
                    <button type="button" data-testid="wip-copy-banner"
                            onClick={() => navigate(`/jobs/${job.wip_copy_id}`)}
                            className="mt-4 w-full text-left inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-sm hover:bg-amber-100 transition-colors">
                        <Hourglass size={14} /> Also on the Work in progress list — tap to open the copy
                    </button>
                )}
                {job.wip_source_job_id && job.is_wip && (
                    <button type="button" data-testid="wip-source-link"
                            onClick={() => navigate(`/jobs/${job.wip_source_job_id}`)}
                            className="mt-4 w-full text-left inline-flex items-center gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] transition-colors">
                        <Hourglass size={14} /> Copied from a scheduled job — tap to open the original
                    </button>
                )}

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <InfoRow icon={<Clock size={14} />} label={job.is_pipeline || job.is_wip ? "Timing" : "Scheduled"}>
                        {job.is_wip ? (
                            <div data-testid="wip-timing-readout">
                                <div className="text-[#9A6B12] font-semibold">Work in progress — awaiting confirmation</div>
                                <div className="text-xs text-[var(--text-muted)]">
                                    {job.scheduled_at ? `Last booked ${fmtDT(job.scheduled_at)}` : "Not on the schedule until rebooked"}
                                </div>
                            </div>
                        ) : job.is_pipeline && !job.scheduled_at ? (
                            <>
                                <div className="text-[var(--terracotta)] font-semibold">Pipeline — no date yet</div>
                                <div className="text-xs text-[var(--text-muted)]">Set a date to book it into the schedule</div>
                            </>
                        ) : (
                            <>
                                <div>{fmtDT(job.scheduled_at)}</div>
                                <div className="text-xs text-[var(--text-muted)]">{job.duration_minutes} min</div>
                            </>
                        )}
                    </InfoRow>
                    <InfoRow icon={<MapPin size={14} />} label="Address">{job.address}</InfoRow>
                    <InfoRow icon={<User size={14} />} label="Assigned to">
                        {job.assigned_to_name || <span className="text-[var(--text-muted)]">Unassigned</span>}
                    </InfoRow>
                    {job.helper_names && job.helper_names.length > 0 && (
                        <InfoRow icon={<User size={14} />} label="Other operatives on site">
                            <span className="inline-flex flex-wrap gap-1.5" data-testid="job-helpers">
                                {job.helper_names.map((n, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full border border-[var(--border-soft)] bg-white text-xs">
                                        {job.helper_colors?.[i] && (
                                            <span className="h-2 w-2 rounded-full" style={{ background: job.helper_colors[i] }} />
                                        )}
                                        {n}
                                    </span>
                                ))}
                            </span>
                        </InfoRow>
                    )}
                </div>

                {job.notes && (
                    <div className="mt-6 p-4 rounded-xl" style={{ background: "var(--pine-light)" }}>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--pine)] font-semibold">Notes</div>
                        <div className="text-sm mt-1 whitespace-pre-line">{job.notes}</div>
                    </div>
                )}

                {job.notes2 && (
                    <div data-testid="job-notes2-readout" className="mt-4 p-4 rounded-xl" style={{ background: "var(--pine-light)" }}>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--pine)] font-semibold">Additional notes</div>
                        <div className="text-sm mt-1 whitespace-pre-line">{job.notes2}</div>
                    </div>
                )}

                {Array.isArray(job.handover_notes) && job.handover_notes.length > 0 && (
                    <div data-testid="handover-notes-readout" className="mt-4 p-4 rounded-xl border border-[#F4C69C] bg-[#FFF7E6]">
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--terracotta)] font-semibold">
                            Hand-over notes
                        </div>
                        <div className="mt-2 space-y-2">
                            {job.handover_notes.map((h, i) => (
                                <div key={i} className="text-sm" data-testid={`handover-note-${i}`}>
                                    <div className="font-semibold text-[var(--text-primary)]">
                                        {h.user_name}
                                        {h.created_at && (
                                            <span className="ml-2 text-[11px] font-normal text-[var(--text-muted)]">
                                                · {new Date(h.created_at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="whitespace-pre-line text-[var(--text-primary)]">{h.note}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {job.is_pipeline && job.pipeline_reason && (
                    <div
                        data-testid="pipeline-reason-readout"
                        className="mt-4 p-4 rounded-xl border border-[#F4C69C] bg-[#FFF7E6]">
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--terracotta)] font-semibold">Waiting because…</div>
                        <div className="text-sm mt-1">{job.pipeline_reason}</div>
                    </div>
                )}

                {(job.updated_at || job.updated_by_name) && (
                    <div
                        data-testid="job-last-edited"
                        className="mt-4 text-[11px] text-[var(--text-muted)] italic">
                        Last edited by {job.updated_by_name || "someone"}
                        {job.updated_at && (
                            <> · {new Date(job.updated_at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</>
                        )}
                    </div>
                )}
            </div>

            {/* Checklist */}
            {client && <ClientDossier client={client} canOpenFullRecord={isManager} />}

            <ClockInOutPanel
                job={job}
                // Managers can clock into/out of any job in their workspace
                // (backend enforces the same rule). Operatives are only in
                // control if they're the assignee or a helper.
                canControl={isManager || job.assigned_to === user.id || (job.helper_ids || []).includes(user.id)}
                onUpdate={setJob}
            />

            {job.checklist?.length > 0 && (
                <section className="card-flat p-6" data-testid="checklist-section">
                    <input
                        ref={checklistPhotoInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={onChecklistPhotoFile}
                        data-testid="checklist-photo-input"
                    />
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">Checklist</h3>
                        <span className="text-xs text-[var(--text-muted)]">
                            {done.size} / {job.checklist.length} done
                        </span>
                    </div>
                    <ul className="space-y-2">
                        {job.checklist.map((item, i) => {
                            const target = Math.max(1, Number((job.checklist_counts || {})[item] || 1));
                            const current = Math.max(0, Number((job.checklist_progress || {})[item] || 0));
                            const isDone = current >= target;
                            const requiresPhoto = (job.checklist_photo_required || []).includes(item);
                            const photoId = (job.checklist_item_photos || {})[item];
                            const proofPhoto = photoId ? photos.find(p => p.id === photoId) : null;
                            const busy = checklistBusyItem === item;
                            const proofMissing = requiresPhoto && !photoId;
                            return (
                                <li key={i}>
                                    <div
                                        data-testid={`checklist-item-${i}`}
                                        data-item-done={isDone ? "true" : "false"}
                                        data-item-progress={`${current}/${target}`}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                                            isDone
                                                ? "border-[var(--pine)] bg-[var(--pine-light)]"
                                                : proofMissing
                                                    ? "border-[var(--terracotta)] bg-[#FFF7E6]"
                                                    : "border-[var(--border-soft)] hover:border-[var(--pine)]"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            data-testid={`checklist-item-tick-${i}`}
                                            onClick={() => isDone ? resetChecklistItem(item) : tickChecklistItem(item)}
                                            disabled={busy}
                                            className={`h-6 w-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                                                isDone
                                                    ? "bg-[var(--pine)] border-[var(--pine)] text-white"
                                                    : "border-[var(--border-soft)] hover:border-[var(--pine)]"
                                            } disabled:opacity-50`}
                                            aria-label={isDone ? "Reset item" : "Tick item"}>
                                            {isDone && <CheckCircle2 size={14} />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm flex items-center gap-2 flex-wrap ${isDone ? "line-through text-[var(--text-muted)]" : ""}`}>
                                                <span className="break-words">{item}</span>
                                                {requiresPhoto && (
                                                    <span
                                                        data-testid={`checklist-item-photo-badge-${i}`}
                                                        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold border whitespace-nowrap ${
                                                            photoId
                                                                ? "border-[var(--pine)] bg-white text-[var(--pine)]"
                                                                : "border-[var(--terracotta)] bg-white text-[var(--terracotta)]"
                                                        }`}>
                                                        📷 {photoId ? "photo attached" : "photo required"}
                                                    </span>
                                                )}
                                                {target > 1 && (
                                                    <span
                                                        data-testid={`checklist-item-counter-${i}`}
                                                        className="text-[10px] font-semibold tabular-nums rounded-full px-1.5 py-0.5 border border-[var(--border-soft)] bg-white">
                                                        {current}/{target}
                                                    </span>
                                                )}
                                            </div>
                                            {proofPhoto && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const idx = photos.findIndex(p => p.id === proofPhoto.id);
                                                        if (idx >= 0) setLightboxIdx(idx);
                                                    }}
                                                    data-testid={`checklist-item-proof-${i}`}
                                                    className="mt-1 inline-block rounded-lg overflow-hidden border border-[var(--border-soft)] hover:border-[var(--pine)]">
                                                    <img
                                                        src={proofPhoto.data?.startsWith("/api/")
                                                            ? `${process.env.REACT_APP_BACKEND_URL}${proofPhoto.data}`
                                                            : proofPhoto.data}
                                                        alt={`${item} proof`}
                                                        className="h-14 w-14 object-cover"
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        {!isDone && target > 1 && (
                                            <button
                                                type="button"
                                                data-testid={`checklist-item-advance-${i}`}
                                                onClick={() => tickChecklistItem(item)}
                                                disabled={busy}
                                                className="btn-ghost text-xs !px-3 shrink-0 disabled:opacity-50">
                                                {busy ? "…" : `Tick (${current + 1}/${target})`}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            {/* Photos */}
            <section className="card-flat p-6">
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                        <Camera size={16} /> Photos
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                        {photos.length > 0 && !selectMode && (
                            <>
                                <button
                                    data-testid="photos-select-btn"
                                    onClick={() => { setSelectMode(true); setSelectedPhotos([]); }}
                                    className="btn-ghost inline-flex items-center gap-2 text-sm"
                                    title="Pick specific photos to download">
                                    <CheckSquare size={14} />
                                    Select
                                </button>
                                <button
                                    data-testid="download-album-btn"
                                    onClick={downloadAlbum}
                                    disabled={downloadingPdf}
                                    className="btn-ghost inline-flex items-center gap-2 text-sm"
                                    title="Download all photos as a PDF album">
                                    <Download size={14} />
                                    {downloadingPdf ? "Building…" : "Download album"}
                                </button>
                            </>
                        )}
                        {selectMode && (
                            <>
                                <span className="text-xs text-[var(--text-muted)]" data-testid="photos-selected-count">
                                    {selectedPhotos.length} selected
                                </span>
                                <button
                                    data-testid="photos-select-all-btn"
                                    onClick={() => setSelectedPhotos(selectedPhotos.length === photos.length ? [] : photos.map(p => p.id))}
                                    className="btn-ghost inline-flex items-center gap-2 text-sm">
                                    {selectedPhotos.length === photos.length ? "Clear" : "Select all"}
                                </button>
                                <button
                                    data-testid="download-selected-btn"
                                    onClick={downloadSelectedZip}
                                    disabled={downloadingZip || !selectedPhotos.length}
                                    className="btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-50">
                                    <Download size={14} />
                                    {downloadingZip ? "Zipping…" : "Download selected"}
                                </button>
                                <button
                                    data-testid="photos-cancel-select-btn"
                                    onClick={() => { setSelectMode(false); setSelectedPhotos([]); }}
                                    className="btn-ghost inline-flex items-center gap-2 text-sm">
                                    Cancel
                                </button>
                            </>
                        )}
                        {!selectMode && (
                            <label className="btn-accent inline-flex items-center gap-2 cursor-pointer text-sm">
                                <input ref={fileRef} type="file" accept="image/*" multiple
                                       className="hidden" onChange={onFile} disabled={uploading}
                                       data-testid="photo-input" />
                                <Camera size={14} />
                                {uploading ? "Uploading…" : "Add photos"}
                            </label>
                        )}
                    </div>
                </div>
                {photos.length === 0 ? (
                    <div className="text-sm text-[var(--text-muted)]">No photos yet. Snap a before/after to keep everyone in the loop.</div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photos-grid">
                        {photos.map((p, idx) => {
                            const isSelected = selectedPhotos.includes(p.id);
                            const src = p.data?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.data}` : p.data;
                            return (
                                <div
                                    key={p.id}
                                    data-testid={`photo-tile-${p.id}`}
                                    className={`relative group rounded-xl overflow-hidden border transition-all ${
                                        isSelected
                                            ? "border-[var(--pine)] ring-2 ring-[var(--pine)]"
                                            : "border-[var(--border-soft)]"
                                    }`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => selectMode ? togglePhotoSelected(p.id) : setLightboxIdx(idx)}
                                        className="block w-full text-left focus:outline-none"
                                        aria-label={selectMode ? "Toggle selection" : "View photo"}
                                        data-testid={selectMode ? `photo-checkbox-${p.id}` : `photo-view-${p.id}`}
                                    >
                                        <img
                                            src={src}
                                            alt={p.caption || p.author_name || "photo"}
                                            className={`w-full h-40 object-cover transition-opacity ${selectMode && !isSelected ? "opacity-80" : ""}`}
                                        />
                                    </button>
                                    <div className="absolute inset-x-0 bottom-0 p-2 text-xs bg-gradient-to-t from-black/60 to-transparent text-white truncate pointer-events-none">
                                        {p.author_name}
                                    </div>
                                    {selectMode && (
                                        <div
                                            className={`absolute top-2 left-2 h-6 w-6 rounded-full flex items-center justify-center text-white shadow pointer-events-none ${
                                                isSelected ? "bg-[var(--pine)]" : "bg-black/40 ring-1 ring-white/70"
                                            }`}
                                        >
                                            {isSelected ? <CheckSquare size={14} /> : <SquareIcon size={14} />}
                                        </div>
                                    )}
                                    {!selectMode && (
                                        <>
                                            <button
                                                type="button"
                                                data-testid={`download-photo-${p.id}`}
                                                onClick={(e) => { e.stopPropagation(); downloadSinglePhoto(p); }}
                                                title="Download this photo"
                                                aria-label="Download photo"
                                                className="absolute top-2 left-2 p-1.5 rounded-full bg-white/90 text-[var(--pine)] opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Download size={12} />
                                            </button>
                                            {(isManager || p.author_id === user.id) && (
                                                <button
                                                    data-testid={`delete-photo-${p.id}`}
                                                    onClick={(e) => { e.stopPropagation(); deletePhoto(p.id); }}
                                                    className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    aria-label="Delete photo">
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Full-size photo lightbox. Opens when any thumbnail is clicked
                outside select mode. Supports prev/next, download, and ESC/
                arrow-key navigation via onKeyDown on the wrapper. */}
            {lightboxIdx >= 0 && lightboxIdx < photos.length && (() => {
                const p = photos[lightboxIdx];
                const src = p.data?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.data}` : p.data;
                const hasPrev = lightboxIdx > 0;
                const hasNext = lightboxIdx < photos.length - 1;
                return (
                    <div
                        data-testid="photo-lightbox"
                        role="dialog"
                        aria-modal="true"
                        tabIndex={-1}
                        ref={(el) => el && el.focus()}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") setLightboxIdx(-1);
                            else if (e.key === "ArrowLeft" && hasPrev) setLightboxIdx(i => i - 1);
                            else if (e.key === "ArrowRight" && hasNext) setLightboxIdx(i => i + 1);
                        }}
                        className="fixed inset-0 z-50 bg-black/85 flex flex-col outline-none"
                        onClick={() => setLightboxIdx(-1)}
                    >
                        <div className="flex items-center justify-between gap-3 p-4 text-white" onClick={(e) => e.stopPropagation()}>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold truncate">{p.caption || p.author_name || "Photo"}</div>
                                <div className="text-xs text-white/70 truncate">
                                    {p.author_name}{p.created_at ? ` · ${new Date(p.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
                                    {" · "}{lightboxIdx + 1} of {photos.length}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="lightbox-download-btn"
                                    onClick={() => downloadSinglePhoto(p)}
                                    className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 text-white">
                                    <Download size={14} /> Download
                                </button>
                                <button
                                    data-testid="lightbox-close-btn"
                                    onClick={() => setLightboxIdx(-1)}
                                    className="inline-flex items-center gap-2 rounded-full p-2 bg-white/10 hover:bg-white/20 text-white"
                                    aria-label="Close">
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 flex items-center justify-center px-4 pb-6 relative" onClick={(e) => e.stopPropagation()}>
                            {hasPrev && (
                                <button
                                    data-testid="lightbox-prev-btn"
                                    onClick={() => setLightboxIdx(i => i - 1)}
                                    className="absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                                    aria-label="Previous photo">
                                    <ChevronLeft size={22} />
                                </button>
                            )}
                            <img
                                src={src}
                                alt={p.caption || p.author_name || "photo"}
                                className="max-h-[80vh] max-w-full object-contain select-none"
                                draggable={false}
                            />
                            {hasNext && (
                                <button
                                    data-testid="lightbox-next-btn"
                                    onClick={() => setLightboxIdx(i => i + 1)}
                                    className="absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                                    aria-label="Next photo">
                                    <ChevronRight size={22} />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Comments */}
            <section className="card-flat p-6">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-4">
                    <MessageSquare size={16} /> Activity & comments
                </h3>

                <div className="space-y-3 mb-4" data-testid="comments-list">
                    {comments.length === 0 ? (
                        <div className="text-sm text-[var(--text-muted)]">Start the conversation — share on-site updates here.</div>
                    ) : comments.map(c => (
                        <div key={c.id} className="flex gap-3">
                            <div className="h-8 w-8 rounded-full bg-[var(--pine-light)] text-[var(--pine)] flex items-center justify-center font-semibold text-xs shrink-0">
                                {c.author_name[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="font-semibold">{c.author_name}</span>
                                    <span className="pill status-scheduled capitalize">{c.author_role}</span>
                                    <span className="text-xs text-[var(--text-muted)]">{fmtTime(c.created_at)}</span>
                                </div>
                                <p className="text-sm mt-1 whitespace-pre-line">{c.body}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <form onSubmit={postComment} className="flex gap-2">
                    <input data-testid="comment-input"
                           value={newComment} onChange={e => setNewComment(e.target.value)}
                           className="input-field" placeholder="Type an update…" />
                    <button data-testid="send-comment-btn" type="submit" className="btn-primary inline-flex items-center gap-1.5">
                        <Send size={14} /> Send
                    </button>
                </form>
            </section>

            {isManager && (
                <CreateJobDialog
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    onUpdated={(updated) => setJob(updated)}
                    operatives={operatives}
                    job={job}
                />
            )}

            <QuickNoteDialog
                open={quickNoteOpen}
                onClose={() => setQuickNoteOpen(false)}
                onPosted={(comment) => {
                    setComments(prev => [...prev, comment]);
                    setQuickNoteOpen(false);
                }}
                jobId={job.id}
            />
        </div>
    );
}

function QuickNoteDialog({ open, onClose, onPosted, jobId }) {
    const [kind, setKind] = useState("note"); // 'note' | 'issue'
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setKind("note"); setBody(""); }
    }, [open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        const clean = body.trim();
        if (!clean) return;
        const prefix = kind === "issue" ? "⚠️ Issue: " : "📝 Note: ";
        setBusy(true);
        try {
            const { data } = await api.post(`/jobs/${jobId}/comments`, { body: prefix + clean });
            toast.success(kind === "issue" ? "Issue reported" : "Note added");
            onPosted(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div
                data-testid="quick-note-dialog"
                onClick={e => e.stopPropagation()}
                className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden">
                <div className="p-5 border-b border-[var(--border-soft)] flex items-start justify-between">
                    <div>
                        <h3 className="font-display text-lg font-semibold">Quick update</h3>
                        <p className="text-xs text-[var(--text-secondary)] mt-1">
                            Jot down something the team should know — it goes straight into the job's activity feed.
                        </p>
                    </div>
                    <button type="button" onClick={onClose}
                            className="p-1 rounded-full text-[var(--text-muted)] hover:text-black"
                            aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <button type="button"
                                data-testid="quick-kind-note"
                                onClick={() => setKind("note")}
                                className={`inline-flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                    kind === "note"
                                        ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                        : "bg-white text-[var(--text-secondary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                                }`}>
                            <StickyNote size={14} /> Note
                        </button>
                        <button type="button"
                                data-testid="quick-kind-issue"
                                onClick={() => setKind("issue")}
                                className={`inline-flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                    kind === "issue"
                                        ? "bg-[var(--terracotta)] text-white border-[var(--terracotta)]"
                                        : "bg-white text-[var(--text-secondary)] border-[var(--border-soft)] hover:border-[var(--terracotta)]"
                                }`}>
                            <AlertOctagon size={14} /> Issue
                        </button>
                    </div>
                    <textarea
                        data-testid="quick-note-input"
                        autoFocus
                        value={body}
                        onChange={e => setBody(e.target.value)}
                        placeholder={kind === "issue"
                            ? "e.g. Vacuum bag broken, will replace tomorrow…"
                            : "e.g. Front gate wedged open — code not needed today."}
                        className="input-field min-h-[100px] resize-y w-full text-sm"
                        required />
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button
                            data-testid="quick-note-submit"
                            type="submit"
                            className={kind === "issue" ? "btn-accent inline-flex items-center gap-1.5" : "btn-primary inline-flex items-center gap-1.5"}
                            disabled={busy || !body.trim()}>
                            <Send size={14} /> {busy ? "Sending…" : (kind === "issue" ? "Report issue" : "Post note")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function InfoRow({ icon, label, children }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold inline-flex items-center gap-1.5">
                {icon} {label}
            </div>
            <div className="mt-1 text-sm">{children}</div>
        </div>
    );
}
